import React, { useState, useMemo } from 'react';
import { 
  X, ChevronRight, ChevronLeft, Check, Sparkles, Crown,
  Calendar, Flag, Search, Users, Plus, Trash2, GripVertical,
  Target, GitBranch, AlertCircle, CheckCircle2, Eye, 
  ArrowUpRight, Layers
} from 'lucide-react';
import { COGNITIVE_LANES, teamMembers } from '../mock';
import { DESIGN } from './DesignSystem';

// ================================================
// STEP INDICATOR
// ================================================
const StepIndicator = ({ currentStep, totalSteps }) => {
  const steps = [
    { num: 1, label: 'Define' },
    { num: 2, label: 'Team' },
    { num: 3, label: 'Structure' }
  ];
  const progress = ((currentStep) / totalSteps) * 100;

  return (
    <div className="mb-8">
      {/* Progress bar */}
      <div 
        className="h-1 rounded-full mb-6 overflow-hidden"
        style={{ backgroundColor: DESIGN.colors.border }}
      >
        <div 
          className="h-full rounded-full transition-all duration-500"
          style={{ 
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`
          }}
        />
      </div>
      
      {/* Step circles */}
      <div className="flex items-center justify-between">
        {steps.map((step, idx) => (
          <div key={step.num} className="flex items-center">
            <div className="flex flex-col items-center">
              <div 
                className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm transition-all ${
                  currentStep >= step.num ? 'scale-100' : 'scale-90'
                }`}
                style={{ 
                  background: currentStep >= step.num 
                    ? `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`
                    : DESIGN.colors.background,
                  color: currentStep >= step.num ? 'white' : DESIGN.colors.textMuted,
                  boxShadow: currentStep >= step.num ? DESIGN.shadows.glowPrimary : 'none',
                  border: currentStep >= step.num ? 'none' : `2px solid ${DESIGN.colors.border}`
                }}
              >
                {currentStep > step.num ? <Check className="w-5 h-5" /> : step.num}
              </div>
              <span 
                className="text-xs font-bold mt-2 uppercase tracking-wider"
                style={{ color: currentStep >= step.num ? DESIGN.colors.primary : DESIGN.colors.textMuted }}
              >
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div 
                className="w-24 h-0.5 mx-4 rounded-full transition-all"
                style={{ 
                  backgroundColor: currentStep > step.num ? DESIGN.colors.primary : DESIGN.colors.border
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================
// COGNITIVE LANE CARD (Selectable)
// ================================================
const CognitiveLaneCard = ({ lane, isSelected, isPrimary, onSelect, onSetPrimary, showPrimaryOption }) => {
  const laneDescriptions = {
    'vision': 'Thinking about where the organization should go and what opportunities to pursue',
    'strategy': 'Defining how to structure and approach complex challenges',
    'creative': 'Generating ideas, concepts, and innovative solutions',
    'marketing': 'Communicating value and building audience connection',
    'operations': 'Building systems and processes that enable efficient execution',
    'execution': 'Getting things done, hitting deadlines, and maintaining quality',
    'revenue': 'Driving sales, partnerships, and monetization strategies',
    'relationships': 'Building connections with stakeholders, partners, and communities',
    'culture': 'Shaping team dynamics, values, and organizational health',
    'finance': 'Managing budgets, analysis, and financial planning'
  };

  return (
    <div 
      onClick={onSelect}
      className={`relative p-4 rounded-xl cursor-pointer transition-all duration-200 ${
        isSelected ? 'ring-2' : 'hover:shadow-md'
      }`}
      style={{ 
        backgroundColor: isSelected ? `${lane.color}12` : DESIGN.colors.card,
        borderLeft: `4px solid ${lane.color}`,
        border: isSelected ? `2px solid ${lane.color}` : `1px solid ${DESIGN.colors.border}`,
        boxShadow: isSelected ? `0 0 20px ${lane.color}25` : 'none'
      }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: lane.color, boxShadow: `0 0 8px ${lane.color}60` }}
            />
            <span className="font-bold text-sm" style={{ color: DESIGN.colors.textPrimary }}>
              {lane.name}
            </span>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: DESIGN.colors.textMuted }}>
            {laneDescriptions[lane.id]}
          </p>
        </div>
        
        {isSelected && (
          <div className="flex items-center gap-2">
            {showPrimaryOption && (
              <button
                onClick={(e) => { e.stopPropagation(); onSetPrimary(); }}
                className={`text-xs font-bold px-2 py-1 rounded-lg transition-all ${
                  isPrimary ? 'text-white' : ''
                }`}
                style={{ 
                  backgroundColor: isPrimary ? lane.color : `${lane.color}20`,
                  color: isPrimary ? 'white' : lane.color
                }}
              >
                {isPrimary ? 'Primary' : 'Set Primary'}
              </button>
            )}
            <div 
              className="w-6 h-6 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: lane.color }}
            >
              <Check className="w-4 h-4 text-white" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ================================================
// TEAM MEMBER SEARCH RESULT
// ================================================
const TeamMemberSearchResult = ({ member, onAdd, isAdded }) => {
  const primaryLane = Object.entries(member.lane_proficiency || {})
    .sort((a, b) => b[1].localeCompare(a[1]))[0];
  const lane = COGNITIVE_LANES.find(l => l.id === primaryLane?.[0]);

  return (
    <div 
      className="flex items-center justify-between p-3 rounded-xl hover:shadow-md transition-all cursor-pointer"
      style={{ backgroundColor: DESIGN.colors.background }}
      onClick={() => !isAdded && onAdd(member)}
    >
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm"
          style={{ 
            background: `linear-gradient(135deg, ${lane?.color || DESIGN.colors.primary}80 0%, ${lane?.color || DESIGN.colors.primary} 100%)`,
            color: 'white'
          }}
        >
          {member.avatar}
        </div>
        <div>
          <p className="font-bold text-sm" style={{ color: DESIGN.colors.textPrimary }}>
            {member.name}
          </p>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
            {member.title}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {lane && (
          <span 
            className="text-xs font-medium px-2 py-1 rounded-lg"
            style={{ backgroundColor: `${lane.color}15`, color: lane.color }}
          >
            {lane.name}
          </span>
        )}
        {isAdded ? (
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: DESIGN.colors.primarySubtle }}
          >
            <Check className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
          </div>
        ) : (
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
            style={{ backgroundColor: DESIGN.colors.card }}
          >
            <Plus className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
          </div>
        )}
      </div>
    </div>
  );
};

// ================================================
// TEAM ROSTER MEMBER
// ================================================
const TeamRosterMember = ({ member, assignedLane, isOwner, onAssignLane, onSetOwner, onRemove, availableLanes }) => {
  const [showLaneDropdown, setShowLaneDropdown] = useState(false);
  const lane = COGNITIVE_LANES.find(l => l.id === assignedLane);

  return (
    <div 
      className="flex items-center justify-between p-3 rounded-xl"
      style={{ backgroundColor: DESIGN.colors.background }}
    >
      <div className="flex items-center gap-3">
        <div className="relative">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm"
            style={{ 
              background: lane 
                ? `linear-gradient(135deg, ${lane.color}80 0%, ${lane.color} 100%)`
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            {member.avatar}
          </div>
          {isOwner && (
            <div 
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#F59E0B', boxShadow: '0 2px 8px rgba(245, 158, 11, 0.6)' }}
            >
              <Crown className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
        <div>
          <p className="font-bold text-sm" style={{ color: DESIGN.colors.textPrimary }}>
            {member.name}
          </p>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
            {member.title}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Lane Assignment Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowLaneDropdown(!showLaneDropdown)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:shadow-sm"
            style={{ 
              backgroundColor: lane ? `${lane.color}15` : DESIGN.colors.card,
              color: lane ? lane.color : DESIGN.colors.textMuted,
              border: `1px solid ${lane ? lane.color : DESIGN.colors.border}`
            }}
          >
            {lane ? lane.name : 'Assign Lane'}
            <ChevronRight className={`w-3 h-3 transition-transform ${showLaneDropdown ? 'rotate-90' : ''}`} />
          </button>
          
          {showLaneDropdown && (
            <div 
              className="absolute right-0 top-full mt-1 w-48 rounded-xl shadow-xl z-20 max-h-48 overflow-auto"
              style={{ backgroundColor: DESIGN.colors.card }}
            >
              {availableLanes.map(l => (
                <button
                  key={l.id}
                  onClick={() => { onAssignLane(l.id); setShowLaneDropdown(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors"
                >
                  <div 
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: l.color }}
                  />
                  {l.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Owner Toggle */}
        <button
          onClick={onSetOwner}
          className={`p-2 rounded-lg transition-all ${isOwner ? '' : 'hover:bg-gray-100'}`}
          style={{ 
            backgroundColor: isOwner ? 'rgba(245, 158, 11, 0.15)' : 'transparent'
          }}
          title={isOwner ? 'Owner' : 'Make Owner'}
        >
          <Crown 
            className="w-4 h-4" 
            style={{ color: isOwner ? '#F59E0B' : DESIGN.colors.textMuted }} 
          />
        </button>

        {/* Remove Button */}
        <button
          onClick={onRemove}
          className="p-2 rounded-lg hover:bg-red-50 transition-all"
        >
          <Trash2 className="w-4 h-4" style={{ color: DESIGN.colors.error }} />
        </button>
      </div>
    </div>
  );
};

// ================================================
// COGNITIVE COVERAGE INDICATOR
// ================================================
const CognitiveCoverageIndicator = ({ selectedLanes, teamMembers: roster }) => {
  const coverage = selectedLanes.map(laneId => {
    const lane = COGNITIVE_LANES.find(l => l.id === laneId);
    const assignedMember = roster.find(m => m.assignedLane === laneId);
    return { lane, covered: !!assignedMember, member: assignedMember };
  });

  const coveredCount = coverage.filter(c => c.covered).length;
  const totalCount = coverage.length;

  return (
    <div 
      className="rounded-xl p-4"
      style={{ backgroundColor: DESIGN.colors.background }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>
          Cognitive Coverage
        </span>
        <span 
          className="text-xs font-bold px-2 py-1 rounded-lg"
          style={{ 
            backgroundColor: coveredCount === totalCount ? DESIGN.colors.primarySubtle : 'rgba(245, 158, 11, 0.15)',
            color: coveredCount === totalCount ? DESIGN.colors.primary : '#F59E0B'
          }}
        >
          {coveredCount}/{totalCount} Lanes
        </span>
      </div>
      
      <div className="space-y-2">
        {coverage.map(({ lane, covered, member }) => (
          <div 
            key={lane.id}
            className="flex items-center justify-between p-2 rounded-lg"
            style={{ backgroundColor: DESIGN.colors.card }}
          >
            <div className="flex items-center gap-2">
              <div 
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: lane.color }}
              />
              <span className="text-xs font-medium" style={{ color: DESIGN.colors.textPrimary }}>
                {lane.name}
              </span>
            </div>
            {covered ? (
              <div className="flex items-center gap-1.5">
                <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                  {member?.member?.name}
                </span>
                <CheckCircle2 className="w-4 h-4" style={{ color: DESIGN.colors.success }} />
              </div>
            ) : (
              <span className="text-xs font-medium" style={{ color: '#F59E0B' }}>
                No contributor
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================
// MILESTONE ITEM
// ================================================
const MilestoneItem = ({ milestone, index, onUpdate, onRemove, teamRoster }) => {
  return (
    <div 
      className="flex items-center gap-3 p-4 rounded-xl group"
      style={{ backgroundColor: DESIGN.colors.background }}
    >
      <div 
        className="cursor-grab text-gray-300 hover:text-gray-500"
        title="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      
      <div className="flex-1 grid grid-cols-3 gap-3">
        <input
          type="text"
          value={milestone.name}
          onChange={(e) => onUpdate({ ...milestone, name: e.target.value })}
          placeholder="Milestone name"
          className="px-3 py-2 rounded-lg text-sm font-medium focus:outline-none focus:ring-2"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`,
            color: DESIGN.colors.textPrimary
          }}
        />
        
        <input
          type="date"
          value={milestone.dueDate}
          onChange={(e) => onUpdate({ ...milestone, dueDate: e.target.value })}
          className="px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`,
            color: DESIGN.colors.textPrimary
          }}
        />

        <select
          value={milestone.assigneeId || ''}
          onChange={(e) => onUpdate({ ...milestone, assigneeId: e.target.value })}
          className="px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`,
            color: DESIGN.colors.textPrimary
          }}
        >
          <option value="">Assignee (optional)</option>
          {teamRoster.map(m => (
            <option key={m.member.id} value={m.member.id}>{m.member.name}</option>
          ))}
        </select>
      </div>

      <button
        onClick={onRemove}
        className="p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
      >
        <Trash2 className="w-4 h-4" style={{ color: DESIGN.colors.error }} />
      </button>
    </div>
  );
};

// ================================================
// SUCCESS METRIC FIELD
// ================================================
const SuccessMetricField = ({ metric, onUpdate }) => {
  return (
    <div className="flex items-center gap-3">
      <input
        type="text"
        value={metric.label}
        onChange={(e) => onUpdate({ ...metric, label: e.target.value })}
        placeholder="Metric label (e.g., Campaign Reach)"
        className="flex-1 px-4 py-3 rounded-xl text-sm font-medium focus:outline-none focus:ring-2"
        style={{ 
          backgroundColor: DESIGN.colors.background,
          border: `1px solid ${DESIGN.colors.border}`,
          color: DESIGN.colors.textPrimary
        }}
      />
      <input
        type="text"
        value={metric.target}
        onChange={(e) => onUpdate({ ...metric, target: e.target.value })}
        placeholder="Target value"
        className="w-32 px-4 py-3 rounded-xl text-sm font-medium focus:outline-none focus:ring-2"
        style={{ 
          backgroundColor: DESIGN.colors.background,
          border: `1px solid ${DESIGN.colors.border}`,
          color: DESIGN.colors.textPrimary
        }}
      />
    </div>
  );
};

// ================================================
// REVIEW SUMMARY
// ================================================
const ReviewSummary = ({ data }) => {
  const primaryLane = COGNITIVE_LANES.find(l => l.id === data.primaryLane);
  const selectedLanes = data.selectedLanes.map(id => COGNITIVE_LANES.find(l => l.id === id)).filter(Boolean);

  return (
    <div 
      className="rounded-xl p-5 space-y-4"
      style={{ backgroundColor: DESIGN.colors.background }}
    >
      <h4 className="text-sm font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>
        Review Summary
      </h4>

      <div className="space-y-3">
        <div>
          <p className="text-xs mb-1" style={{ color: DESIGN.colors.textMuted }}>Name</p>
          <p className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>{data.name || '—'}</p>
        </div>

        <div>
          <p className="text-xs mb-1" style={{ color: DESIGN.colors.textMuted }}>Description</p>
          <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>{data.description || '—'}</p>
        </div>

        <div>
          <p className="text-xs mb-2" style={{ color: DESIGN.colors.textMuted }}>Cognitive Lanes</p>
          <div className="flex flex-wrap gap-2">
            {selectedLanes.map(lane => (
              <span 
                key={lane.id}
                className="text-xs font-medium px-2 py-1 rounded-lg flex items-center gap-1"
                style={{ 
                  backgroundColor: `${lane.color}15`,
                  color: lane.color,
                  border: lane.id === data.primaryLane ? `2px solid ${lane.color}` : 'none'
                }}
              >
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: lane.color }} />
                {lane.name}
                {lane.id === data.primaryLane && <span className="font-bold ml-1">Primary</span>}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs mb-1" style={{ color: DESIGN.colors.textMuted }}>Due Date</p>
            <p className="text-sm font-medium" style={{ color: DESIGN.colors.textPrimary }}>
              {data.dueDate ? new Date(data.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs mb-1" style={{ color: DESIGN.colors.textMuted }}>Priority</p>
            <p className="text-sm font-medium" style={{ color: DESIGN.colors.textPrimary }}>{data.priority || '—'}</p>
          </div>
        </div>

        <div>
          <p className="text-xs mb-2" style={{ color: DESIGN.colors.textMuted }}>Team ({data.teamRoster.length})</p>
          <div className="flex -space-x-2">
            {data.teamRoster.slice(0, 6).map((m, i) => (
              <div 
                key={m.member.id}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold border-2 border-white"
                style={{ 
                  background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
                  color: 'white'
                }}
              >
                {m.member.avatar}
              </div>
            ))}
            {data.teamRoster.length > 6 && (
              <span className="text-xs font-medium ml-2" style={{ color: DESIGN.colors.textMuted }}>
                +{data.teamRoster.length - 6}
              </span>
            )}
          </div>
        </div>

        <div>
          <p className="text-xs mb-1" style={{ color: DESIGN.colors.textMuted }}>
            Milestones ({data.milestones.filter(m => m.name).length})
          </p>
          <div className="flex flex-wrap gap-1">
            {data.milestones.filter(m => m.name).map((m, i) => (
              <span 
                key={i}
                className="text-xs px-2 py-0.5 rounded-md"
                style={{ backgroundColor: DESIGN.colors.card, color: DESIGN.colors.textSecondary }}
              >
                {m.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ================================================
// MAIN NEW COLLABORATION MODAL
// ================================================
const NewCollaborationModal = ({ isOpen, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Step 1 State
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedLanes, setSelectedLanes] = useState([]);
  const [primaryLane, setPrimaryLane] = useState(null);
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('Medium');

  // Step 2 State
  const [searchQuery, setSearchQuery] = useState('');
  const [teamRoster, setTeamRoster] = useState([]);
  const [ownerId, setOwnerId] = useState(null);

  // Step 3 State
  const [milestones, setMilestones] = useState([{ name: '', dueDate: '', assigneeId: null }]);
  const [successMetrics, setSuccessMetrics] = useState([
    { label: '', target: '' },
    { label: '', target: '' },
    { label: '', target: '' }
  ]);
  const [linkedProject, setLinkedProject] = useState('');
  const [visibility, setVisibility] = useState('team');

  // Filtered team members for search
  const filteredMembers = useMemo(() => {
    if (!searchQuery) return teamMembers.slice(0, 5);
    return teamMembers.filter(m => 
      m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery]);

  // Smart milestone suggestions based on selected lanes
  const suggestedMilestones = useMemo(() => {
    const suggestions = [];
    if (selectedLanes.includes('marketing')) suggestions.push('Strategy & Brief');
    if (selectedLanes.includes('creative')) suggestions.push('Creative Production');
    if (selectedLanes.includes('revenue')) suggestions.push('Campaign Launch', 'Performance Review');
    if (selectedLanes.includes('strategy')) suggestions.push('Research & Discovery', 'Strategic Alignment');
    if (selectedLanes.includes('execution')) suggestions.push('Implementation', 'Quality Check');
    if (selectedLanes.includes('operations')) suggestions.push('Process Setup', 'System Integration');
    return suggestions.length > 0 ? suggestions : ['Planning', 'Execution', 'Review'];
  }, [selectedLanes]);

  // Handlers
  const handleLaneSelect = (laneId) => {
    setSelectedLanes(prev => {
      if (prev.includes(laneId)) {
        const newLanes = prev.filter(id => id !== laneId);
        if (primaryLane === laneId) setPrimaryLane(newLanes[0] || null);
        return newLanes;
      }
      return [...prev, laneId];
    });
  };

  const handleSetPrimary = (laneId) => {
    setPrimaryLane(laneId);
  };

  const handleAddTeamMember = (member) => {
    if (!teamRoster.find(m => m.member.id === member.id)) {
      setTeamRoster(prev => [...prev, { member, assignedLane: null }]);
      if (!ownerId) setOwnerId(member.id);
    }
  };

  const handleRemoveTeamMember = (memberId) => {
    setTeamRoster(prev => prev.filter(m => m.member.id !== memberId));
    if (ownerId === memberId) {
      setOwnerId(teamRoster.find(m => m.member.id !== memberId)?.member.id || null);
    }
  };

  const handleAssignLane = (memberId, laneId) => {
    setTeamRoster(prev => prev.map(m => 
      m.member.id === memberId ? { ...m, assignedLane: laneId } : m
    ));
  };

  const handleAddMilestone = () => {
    setMilestones(prev => [...prev, { name: '', dueDate: '', assigneeId: null }]);
  };

  const handleUpdateMilestone = (index, milestone) => {
    setMilestones(prev => prev.map((m, i) => i === index ? milestone : m));
  };

  const handleRemoveMilestone = (index) => {
    setMilestones(prev => prev.filter((_, i) => i !== index));
  };

  const handleUseSuggestions = () => {
    const newMilestones = suggestedMilestones.map(name => ({ name, dueDate: '', assigneeId: null }));
    setMilestones(newMilestones);
  };

  const handleCreate = async () => {
    setIsCreating(true);
    // Simulate creation
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsCreating(false);
    setShowSuccess(true);
  };

  // Validation
  const canProceedStep1 = name.trim() && selectedLanes.length >= 2 && primaryLane;
  const canProceedStep2 = teamRoster.length >= 2;
  const canProceedStep3 = milestones.filter(m => m.name.trim()).length >= 1;

  if (!isOpen) return null;

  // Success State
  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        <div 
          className="relative w-full max-w-md rounded-3xl p-10 text-center animate-scale-in"
          style={{ backgroundColor: DESIGN.colors.card }}
        >
          <div 
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
              boxShadow: `0 0 60px ${DESIGN.colors.primary}60`
            }}
          >
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          
          <h2 className="text-2xl font-black mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Collaboration Created!
          </h2>
          <p className="text-lg font-bold mb-6" style={{ color: DESIGN.colors.primary }}>
            {name}
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => { onSuccess?.(); onClose(); }}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-white transition-all hover:shadow-lg"
              style={{ 
                background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                boxShadow: DESIGN.shadows.glowPrimary
              }}
            >
              <Eye className="w-5 h-5" />
              View Collaboration
            </button>
            <button
              onClick={onClose}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold transition-all hover:bg-gray-100"
              style={{ 
                backgroundColor: DESIGN.colors.background,
                color: DESIGN.colors.textPrimary
              }}
            >
              <ArrowUpRight className="w-5 h-5" />
              Go to Collab Page
            </button>
          </div>
        </div>

        <style>{`
          @keyframes scale-in {
            from { transform: scale(0.9); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          .animate-scale-in {
            animation: scale-in 0.3s ease-out;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Panel */}
      <div 
        className="relative ml-auto w-full max-w-3xl h-full overflow-auto animate-slide-in"
        style={{ backgroundColor: DESIGN.colors.card }}
      >
        {/* Header */}
        <div 
          className="sticky top-0 z-10 px-8 py-6 border-b"
          style={{ backgroundColor: DESIGN.colors.card, borderColor: DESIGN.colors.border }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                  boxShadow: DESIGN.shadows.glowPrimary
                }}
              >
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-black" style={{ color: DESIGN.colors.textPrimary }}>
                  New Collaboration
                </h2>
                <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>
                  Step {currentStep} of 3
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" style={{ color: DESIGN.colors.textMuted }} />
            </button>
          </div>

          <StepIndicator currentStep={currentStep} totalSteps={3} />
        </div>

        {/* Content */}
        <div className="p-8">
          {/* STEP 1: Define */}
          {currentStep === 1 && (
            <div className="space-y-8">
              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
                  Collaboration Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Give this collaboration a clear name"
                  className="w-full px-5 py-4 rounded-xl text-lg font-medium focus:outline-none focus:ring-2 transition-all"
                  style={{ 
                    backgroundColor: DESIGN.colors.background,
                    border: `2px solid ${DESIGN.colors.border}`,
                    color: DESIGN.colors.textPrimary,
                    '--tw-ring-color': DESIGN.colors.primary
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this collaboration trying to achieve? Describe the outcome, not just the activity."
                  rows={4}
                  className="w-full px-5 py-4 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all resize-none"
                  style={{ 
                    backgroundColor: DESIGN.colors.background,
                    border: `2px solid ${DESIGN.colors.border}`,
                    color: DESIGN.colors.textPrimary
                  }}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="block text-sm font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                      Cognitive Composition
                    </label>
                    <p className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>
                      Select all the cognitive lanes this work will require. This is what makes it a Collaboration.
                    </p>
                  </div>
                </div>

                {selectedLanes.length === 1 && (
                  <div 
                    className="flex items-center gap-2 p-3 rounded-xl mb-4"
                    style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}
                  >
                    <AlertCircle className="w-4 h-4" style={{ color: '#F59E0B' }} />
                    <span className="text-sm" style={{ color: '#B45309' }}>
                      A Collaboration requires multiple cognitive lanes. For a single-lane task, use the Tasks section instead.
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  {COGNITIVE_LANES.map(lane => (
                    <CognitiveLaneCard
                      key={lane.id}
                      lane={lane}
                      isSelected={selectedLanes.includes(lane.id)}
                      isPrimary={primaryLane === lane.id}
                      onSelect={() => handleLaneSelect(lane.id)}
                      onSetPrimary={() => handleSetPrimary(lane.id)}
                      showPrimaryOption={selectedLanes.length >= 2 && selectedLanes.includes(lane.id)}
                    />
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
                    Due Date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                      style={{ 
                        backgroundColor: DESIGN.colors.background,
                        border: `2px solid ${DESIGN.colors.border}`,
                        color: DESIGN.colors.textPrimary
                      }}
                    />
                    <Calendar 
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" 
                      style={{ color: DESIGN.colors.textMuted }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
                    Priority
                  </label>
                  <div className="flex gap-2">
                    {['High', 'Medium', 'Low'].map(p => (
                      <button
                        key={p}
                        onClick={() => setPriority(p)}
                        className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all ${
                          priority === p ? '' : 'hover:bg-gray-50'
                        }`}
                        style={{ 
                          backgroundColor: priority === p 
                            ? p === 'High' ? 'rgba(239, 68, 68, 0.15)' 
                            : p === 'Medium' ? 'rgba(245, 158, 11, 0.15)'
                            : DESIGN.colors.primarySubtle
                            : DESIGN.colors.background,
                          color: priority === p
                            ? p === 'High' ? '#DC2626' 
                            : p === 'Medium' ? '#D97706'
                            : DESIGN.colors.primary
                            : DESIGN.colors.textMuted,
                          border: `2px solid ${priority === p 
                            ? p === 'High' ? '#DC2626' 
                            : p === 'Medium' ? '#D97706'
                            : DESIGN.colors.primary
                            : DESIGN.colors.border}`
                        }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Build Team */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold mb-1" style={{ color: DESIGN.colors.textPrimary }}>
                  Build your cognitive team
                </h3>
                <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>
                  Add team members and assign them to the lanes they will contribute from.
                </p>
              </div>

              {teamRoster.length < 2 && (
                <div 
                  className="flex items-center gap-2 p-3 rounded-xl"
                  style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}
                >
                  <AlertCircle className="w-4 h-4" style={{ color: '#F59E0B' }} />
                  <span className="text-sm" style={{ color: '#B45309' }}>
                    Collaborations involve multiple contributors. Add at least {2 - teamRoster.length} more team member{2 - teamRoster.length > 1 ? 's' : ''}.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                {/* Search Panel */}
                <div>
                  <div className="relative mb-4">
                    <Search 
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" 
                      style={{ color: DESIGN.colors.textMuted }} 
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name or role..."
                      className="w-full pl-11 pr-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: DESIGN.colors.background,
                        border: `2px solid ${DESIGN.colors.border}`,
                        color: DESIGN.colors.textPrimary
                      }}
                    />
                  </div>

                  <div className="space-y-2 max-h-80 overflow-auto">
                    {filteredMembers.map(member => (
                      <TeamMemberSearchResult
                        key={member.id}
                        member={member}
                        onAdd={handleAddTeamMember}
                        isAdded={!!teamRoster.find(m => m.member.id === member.id)}
                      />
                    ))}
                  </div>
                </div>

                {/* Team Roster Panel */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                      Team Roster ({teamRoster.length})
                    </span>
                  </div>

                  {teamRoster.length === 0 ? (
                    <div 
                      className="p-8 rounded-xl text-center"
                      style={{ backgroundColor: DESIGN.colors.background }}
                    >
                      <Users className="w-10 h-10 mx-auto mb-3" style={{ color: DESIGN.colors.textMuted }} />
                      <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>
                        No team members added yet
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {teamRoster.map(({ member, assignedLane }) => (
                        <TeamRosterMember
                          key={member.id}
                          member={member}
                          assignedLane={assignedLane}
                          isOwner={ownerId === member.id}
                          onAssignLane={(laneId) => handleAssignLane(member.id, laneId)}
                          onSetOwner={() => setOwnerId(member.id)}
                          onRemove={() => handleRemoveTeamMember(member.id)}
                          availableLanes={COGNITIVE_LANES.filter(l => selectedLanes.includes(l.id))}
                        />
                      ))}
                    </div>
                  )}

                  {/* Cognitive Coverage */}
                  {teamRoster.length > 0 && selectedLanes.length > 0 && (
                    <div className="mt-4">
                      <CognitiveCoverageIndicator 
                        selectedLanes={selectedLanes}
                        teamMembers={teamRoster}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Set Structure */}
          {currentStep === 3 && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-bold mb-1" style={{ color: DESIGN.colors.textPrimary }}>
                  Structure the work
                </h3>
                <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>
                  Break this collaboration into milestones and define how success will be measured.
                </p>
              </div>

              {/* Milestones */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                    Milestones
                  </label>
                  <button
                    onClick={handleUseSuggestions}
                    className="text-xs font-medium px-3 py-1.5 rounded-lg transition-all hover:shadow-sm"
                    style={{ 
                      backgroundColor: DESIGN.colors.primarySubtle,
                      color: DESIGN.colors.primary
                    }}
                  >
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    Use Suggestions
                  </button>
                </div>

                {milestones.filter(m => m.name.trim()).length === 0 && (
                  <div 
                    className="flex items-center gap-2 p-3 rounded-xl mb-3"
                    style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}
                  >
                    <AlertCircle className="w-4 h-4" style={{ color: '#F59E0B' }} />
                    <span className="text-sm" style={{ color: '#B45309' }}>
                      Add at least one milestone to structure the collaboration's progress.
                    </span>
                  </div>
                )}

                <div className="space-y-2">
                  {milestones.map((milestone, index) => (
                    <MilestoneItem
                      key={index}
                      milestone={milestone}
                      index={index}
                      onUpdate={(m) => handleUpdateMilestone(index, m)}
                      onRemove={() => handleRemoveMilestone(index)}
                      teamRoster={teamRoster}
                    />
                  ))}
                </div>

                <button
                  onClick={handleAddMilestone}
                  className="flex items-center gap-2 w-full py-3 rounded-xl text-sm font-medium mt-3 transition-all hover:bg-gray-50"
                  style={{ 
                    backgroundColor: DESIGN.colors.background,
                    color: DESIGN.colors.textMuted,
                    border: `2px dashed ${DESIGN.colors.border}`
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Add Milestone
                </button>
              </div>

              {/* Success Metrics */}
              <div>
                <label className="block text-sm font-bold mb-3" style={{ color: DESIGN.colors.textPrimary }}>
                  Success Metrics <span className="font-normal" style={{ color: DESIGN.colors.textMuted }}>(optional)</span>
                </label>
                <div className="space-y-3">
                  {successMetrics.map((metric, index) => (
                    <SuccessMetricField
                      key={index}
                      metric={metric}
                      onUpdate={(m) => {
                        const newMetrics = [...successMetrics];
                        newMetrics[index] = m;
                        setSuccessMetrics(newMetrics);
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Visibility */}
              <div>
                <label className="block text-sm font-bold mb-3" style={{ color: DESIGN.colors.textPrimary }}>
                  Visibility
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setVisibility('team')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all ${
                      visibility === 'team' ? '' : 'hover:bg-gray-50'
                    }`}
                    style={{ 
                      backgroundColor: visibility === 'team' ? DESIGN.colors.primarySubtle : DESIGN.colors.background,
                      color: visibility === 'team' ? DESIGN.colors.primary : DESIGN.colors.textMuted,
                      border: `2px solid ${visibility === 'team' ? DESIGN.colors.primary : DESIGN.colors.border}`
                    }}
                  >
                    <Users className="w-4 h-4" />
                    Team Only
                  </button>
                  <button
                    onClick={() => setVisibility('org')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all ${
                      visibility === 'org' ? '' : 'hover:bg-gray-50'
                    }`}
                    style={{ 
                      backgroundColor: visibility === 'org' ? DESIGN.colors.primarySubtle : DESIGN.colors.background,
                      color: visibility === 'org' ? DESIGN.colors.primary : DESIGN.colors.textMuted,
                      border: `2px solid ${visibility === 'org' ? DESIGN.colors.primary : DESIGN.colors.border}`
                    }}
                  >
                    <Eye className="w-4 h-4" />
                    Organization Wide
                  </button>
                </div>
              </div>

              {/* Review Summary */}
              <ReviewSummary
                data={{
                  name,
                  description,
                  selectedLanes,
                  primaryLane,
                  dueDate,
                  priority,
                  teamRoster,
                  milestones
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div 
          className="sticky bottom-0 px-8 py-6 border-t flex items-center justify-between"
          style={{ backgroundColor: DESIGN.colors.card, borderColor: DESIGN.colors.border }}
        >
          <button
            onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : onClose()}
            className="flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all hover:bg-gray-100"
            style={{ color: DESIGN.colors.textMuted }}
          >
            <ChevronLeft className="w-4 h-4" />
            {currentStep > 1 ? 'Back' : 'Cancel'}
          </button>

          {currentStep < 3 ? (
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={currentStep === 1 ? !canProceedStep1 : !canProceedStep2}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                boxShadow: DESIGN.shadows.glowPrimary
              }}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={!canProceedStep3 || isCreating}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                boxShadow: DESIGN.shadows.glowPrimary
              }}
            >
              {isCreating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Create Collaboration
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default NewCollaborationModal;
